import cn from 'classnames';
import useTranslationAndTypograph from 'hooks/useTranslationAndTypograph';
import React from 'react';

import FormattedPrice from 'components/basic/FormattedPrice';
import { BodyM } from 'components/basic/Typography';
import s from 'components/complex/payments/Product/Product.module.scss';
import {
  plansDescription,
  plansDescriptionRu,
} from 'components/complex/payments/Product/constants';
import useSplitYearlyCost from 'components/complex/payments/Product/useSplitYearlyCost';

import type { ProductType } from 'redux-store/slices/products/types';
import type { IntervalType } from 'redux-store/slices/products/types';

import dateToString from 'utils/dateToString';

interface Props {
  product: ProductType;
  isCurrentUserPlan?: boolean;
  nextPayment?: string;
  isRu?: boolean;
}

export default function Description({
  product,
  isCurrentUserPlan,
  nextPayment,
  isRu,
}: Props): JSX.Element {
  const { interval, interval_count } = product.prices[0];
  let description = null;
  const { isNeedToSplitYearPrice } = useSplitYearlyCost();
  const t = useTranslationAndTypograph();
  const descriptionList = isRu ? plansDescriptionRu : plansDescription;

  if (isCurrentUserPlan) {
    const nextPaymentDateString = dateToString(nextPayment);

    if (nextPaymentDateString && !product.isCanceled) {
      description = (
        <>
          {t('Your current plan, next payment')} {dateToString(nextPayment)}
        </>
      );
    } 
    else {
      description = <>{t('Your current plan')}</>;
    }
  } 
  else {
    const intervalKey = `${interval_count}month` as IntervalType;
    if (interval === 'month' && interval_count !== undefined && descriptionList[intervalKey]) {
      description = t(descriptionList[intervalKey]);
    }
    else if (descriptionList[interval]) {
      description = t(descriptionList[interval]);
    }

    if (product.prices[0].interval === 'year' && isNeedToSplitYearPrice) {
      const { currency, price, discount_price } = product.prices[0];

      const currentPrice = discount_price ?? price;

      description = (
        <>
          <FormattedPrice price={currentPrice} currency={currency} />{' '}
          {t('billed once a year')}
        </>
      );
    }
  }

  return (
    <BodyM
      className={cn(s.description, {
        [s.currentPlanDescription]: isCurrentUserPlan,
      })}
    >
      {description}
    </BodyM>
  );
}
